<template>
  <div v-if="event" class="page-container">
    <button
      class="main-button v-btn v-btn--depressed v-btn--round theme--light primary"
      @click="openPrint"
    >
      Print
    </button>
    <p class="date">Vrijeme generiranja izvještaja: {{ todayDate }} {{ currTime }}</p>
    <h1 class="page-title">Izvještaj eventa, {{ event.title }} {{ formatDateTime(event.from) }}</h1>
    <div>
      <p class="organization-data" style="font-weight: 800">
        {{ organization.legalName }}, OIB: {{ organization.OIB }}, {{ organization.address }},
        {{ organization.postalCode }} {{ organization.city }}
      </p>
    </div>
    <div class="divider" />
    <div class="table-info">
      <section>
        <p>Kapacitet dvorane</p>
        <p>{{ Math.max(statistics.venueCapacity, statistics.ticketsInSales) }}</p>
      </section>
      <section>
        <p>Ulaznice u prodaji</p>
        <p>{{ statistics.ticketsInSales }}</p>
      </section>
      <section>
        <p>Ulaznice onemogućene za prodaju</p>
        <p>{{ Math.max(statistics.venueCapacity - statistics.ticketsInSales, 0) }}</p>
      </section>
    </div>
    <div class="divider" />
    <p class="basic">Prodaja po stavkama</p>
    <div class="table-info">
      <section>
        <p>Ukupno gratis ulaznica</p>
        <p>
          {{ totalGratisCount }} ({{
            round((100 * totalGratisCount) / (totalGratisCount + totalSoldCount))
          }}%)
        </p>
      </section>
      <section>
        <p>Ukupno ulaznica kupljenih putem narudžbenice</p>
        <p>
          {{ totalOrderFormCount }} ({{
            round((100 * totalOrderFormCount) / (totalGratisCount + totalSoldCount))
          }}%)
        </p>
      </section>
      <section>
        <p>Online kupnja</p>
        <p>
          {{ totalOnlineSoldCount }} ({{
            round((100 * totalOnlineSoldCount) / (totalGratisCount + totalSoldCount))
          }}%)
        </p>
      </section>
      <section>
        <p>Fizička kupnja</p>
        <p>
          {{ totalOfflineSoldCount }} ({{
            round((100 * totalOfflineSoldCount) / (totalGratisCount + totalSoldCount))
          }}%)
        </p>
      </section>
      <section>
        <p style="font-weight: bold">Ukupno izdanih ulaznica</p>
        <p style="font-weight: bold">
          {{ totalSoldCount + totalGratisCount }} ({{
            round((100 * (totalSoldCount + totalGratisCount)) / statistics.ticketsInSales)
          }}% popunjenost)
        </p>
      </section>
    </div>
    <p v-if="gratisOrders.length" class="basic">Popis gratis ulaznica</p>
    <div class="table-info">
      <section v-for="order in gratisOrders">
        <p>
          {{
            (order.buyer && order.buyer.name) ||
            (order.buyer && order.buyer.email) ||
            'Fizičko prodajno mjesto'
          }}
        </p>
        <p>{{ order.purchases.length }}</p>
      </section>
    </div>
    <p v-if="orderFormOrders && orderFormOrders.length" class="basic">Popis narudženica</p>
    <div class="table-info">
      <section v-for="order in orderFormOrders">
        <p v-if="order.legalEntity">
          {{ order.legalEntity.name }}, OIB: {{ order.legalEntity.OIB }},
          {{ order.legalEntity.address }} {{ order.legalEntity.city }}
          {{ order.legalEntity.zipCode }}
        </p>
        <p v-if="!order.legalEntity">{{ 'Fizičko prodajno mjesto' }}</p>
        <p>{{ order.purchases.length }}</p>
      </section>
    </div>
    <p v-if="refundOrders && refundOrders.length" class="basic">Popis storno narudžbu</p>
    <div class="table-info">
      <section v-for="order in refundOrders">
        <p>
          {{
            order.buyer && order.buyer.fields && order.buyer.fields.firstName
              ? `${order.buyer.fields.firstName} ${order.buyer.fields.lastName}`
              : 'Fizička prodaja'
          }}
        </p>
        <p>{{ order.purchases.length }} kom, {{ calculateOrderPrice(order) }} EUR</p>
      </section>
      <section v-if="refundOrders && refundOrders.length" style="font-weight: bold">
        <p>Ukupno</p>
        <p>{{ totalRefundedCount }} kom, {{ round(totalRefundedAmount) }} EUR</p>
      </section>
    </div>

    <div class="divider" />
    <p class="basic">Prodaja ulaznica</p>
    <div>
      <div class="report-data report-title">
        <p>Ulaznica</p>
        <p>Cijena (EUR)</p>
        <p>Online/Fizičke</p>
        <p>Promo kod popust</p>
        <p>Ukupno Kom</p>
        <p>Ukupno EUR</p>
        <p>Narudžbenica</p>
        <p>Pretplata</p>
      </div>
      <div v-for="(report, i) in reportData" :key="i">
        <div class="report-row">
          <p class="report-data">{{ report.name }}</p>
          <p class="report-data">{{ report.price }}</p>
          <p class="report-data">{{ report.online == '0' ? 'FIZIČKE' : 'ONLINE' }}</p>
          <p class="report-data">
            {{
              report.promo_code_discount_percentage
                ? `${report.promo_code_discount_percentage}%`
                : ''
            }}
            {{
              report.promo_code_discount_amount ? `${report.promo_code_discount_amount} EUR` : ''
            }}
            {{
              !report.promo_code_discount_amount && !report.promo_code_discount_amount ? '-' : ''
            }}
          </p>
          <p class="report-data">{{ parseInt(report.online) + parseInt(report.offline) }}</p>
          <p class="report-data">{{ report.total }}</p>
          <p class="report-data">{{ report.isOrderForm ? 'Narudžbenica' : '' }}</p>
          <p class="report-data">{{ report.prepaidCoupon || '' }}</p>
        </div>
      </div>
    </div>
    <div>
      <div class="report-row">
        <p class="report-data" style="font-weight: 700">Fizičke</p>
        <p class="report-data" style="font-weight: 700">{{ totalOfflineSoldCount }} kom</p>
        <p class="report-data" style="font-weight: 700">{{ totalOfflineSoldAmount }} EUR</p>
      </div>
      <div class="report-row">
        <p class="report-data" style="font-weight: 700">Online</p>
        <p class="report-data" style="font-weight: 700">{{ totalOnlineSoldCount }} kom</p>
        <p class="report-data" style="font-weight: 700">{{ totalOnlineSoldAmount }} EUR</p>
      </div>
      <div class="report-row" style="border-bottom: 1px #333 solid">
        <p class="report-data" style="font-weight: 700">Narudžbenice</p>
        <p class="report-data" style="font-weight: 700">{{ totalOrderFormCount }} kom</p>
        <p class="report-data" style="font-weight: 700">{{ totalOrderFormSoldAmount }} EUR</p>
      </div>
      <div class="report-row" style="border-bottom: 1px #333 solid">
        <p class="report-data" style="font-weight: 700">Ukupno</p>
        <p class="report-data" style="font-weight: 700">{{ totalSoldCount }} kom</p>
        <p class="report-data" style="font-weight: 700">{{ totalSoldAmount }} EUR</p>
      </div>
    </div>
    <div v-if="ordersWithDescription.length">
      <p class="basic">Narudžbe s napomenom</p>
      <div>
        <div class="report-data report-title" style="display: flex">
          <p style="flex: 2">Broj narudžbe</p>
          <p style="flex: 4">Napomena</p>
        </div>
        <div
          v-for="order in ordersWithDescription"
          :key="order.id"
          class="order-with-description report-row report-data"
        >
          <p style="flex: 2">{{ order.id }}</p>
          <p style="flex: 4; line-height: normal">{{ order.description }}</p>
        </div>
      </div>
    </div>
    <div>
      <div class="divider" style="margin-top: 24px" />
      <p class="basic" style="margin: 24px 0">
        Rekapitulacija fizičke prodaje prema načinu plaćanja
      </p>
      <div>
        <div class="report-row">
          <p class="header-data" style="font-weight: 700">Gotovinska plaćanja</p>
        </div>
      </div>
      <div>
        <div class="report-row">
          <p class="report-data">Gotovina</p>
          <p class="report-data">{{ offlineRecapitulationCash.count }} kom</p>
          <p class="report-data text-right">{{ offlineRecapitulationCash.total }} eur</p>
        </div>
      </div>
      <div class="report-row">
        <p class="report-data text-right" style="font-weight: 700">Ukupno</p>
        <p class="report-data" style="font-weight: 700">
          {{ offlineRecapitulationCash.count }} kom
        </p>
        <p class="report-data text-right" style="font-weight: 700">
          {{ offlineRecapitulationCash.total }} eur
        </p>
      </div>
      <div>
        <div class="report-row">
          <p class="header-data" style="font-weight: 700">Platne/kreditne kartice</p>
        </div>
      </div>
      <div v-for="cardRecapitulationData in offlineRecapitulationCards" :key="card">
        <div>
          <div class="report-row">
            <p class="report-data">{{ cardRecapitulationData.bankCardType }}</p>
            <p class="report-data">{{ cardRecapitulationData.count }} kom</p>
            <p class="report-data text-right">{{ cardRecapitulationData.total }} eur</p>
          </div>
        </div>
      </div>
      <div class="report-row">
        <p class="report-data text-right" style="font-weight: 700">Ukupno</p>
        <p class="report-data" style="font-weight: 700">
          {{ offlineRecapitulationCardsAll.count }} kom
        </p>
        <p class="report-data text-right" style="font-weight: 700">
          {{ offlineRecapitulationCardsAll.total }} eur
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { round } from 'mathjs'
import dayjs from 'dayjs'
import { GET_THEATER_EVENT_REPORT } from '../../graphql/purchase.graphql'
import { GET_ORGANIZATION } from '@/graphql/organization.graphql'

export default {
  props: {
    eventId: String,
    organizationSlug: String,
  },
  data() {
    return {
      gratisOrders: [],
      organization: {},
      statistics: {},
      event: {},
      reportData: [],
      orderFormOrders: [],
      ordersWithDescription: [],
      refundOrders: [],
      totalRefundedCount: 0,
      totalRefundedAmount: 0,
      zkmCustomEvents: [2053, 2051, 2052, 2050],
      2053: [6529, 6530],
      2051: [6530],
      2052: [6528],
      2050: [6528, 6529],
      customExtraEvent: {},
      totalSoldAmount: 0,
      totalSoldCount: 0,
      totalOfflineSoldCount: 0,
      totalOfflineSoldAmount: 0,
      totalOrderFormSoldAmount: 0,
      totalOrderFormSoldCount: 0,
      totalOnlineSoldCount: 0,
      totalOnlineSoldAmount: 0,
      totalGratisCount: 0,
      totalOrderFormCount: 0,
      todayDate: dayjs().format('DD.MM.YYYY'),
      currTime: dayjs().format('HH:mm'),
      offlineRecapitulationCash: {
        count: 0,
        total: 0,
      },
      offlineRecapitulationCardsAll: {
        count: 0,
        total: 0,
      },
      offlineRecapitulationCards: [],
    }
  },
  async mounted() {
    this.event = await this.getPurchasesByEvent(this.eventId)
    if (this.zkmCustomEvents.includes(Number(this.eventId))) {
      this.customExtraEvent = await this.getPurchasesByEvent('2247')
      const correctCustomOrders = (this.customExtraEvent.refundOrders || []).filter(o => {
        const correctPurchases = o.purchases.filter(p =>
          this[Number(this.event.event.id)].includes(p.orderItemId),
        )
        o.purchases = correctPurchases.filter(Boolean)

        return correctPurchases.filter(Boolean).length
      })
      this.event.refundOrders = [
        ...(this.event.refundOrders || []),
        ...(correctCustomOrders || []).map(o => {
          const price = Number(o.price.split(' ')[1])

          return { ...o, price: `EUR ${price / 2}` }
        }),
      ]
      const correctCustomReports = (this.customExtraEvent.report || [])
        .map(r => {
          return {
            ...r,
            total: `${Number(r.total) / 2}`,
            price: `${Number(r.price) / 2}`,
            prepaidCoupon: null,
          }
        })
        .filter(r => this[Number(this.event.event.id)].includes(Number(r.orderItemId)))
        .filter(Boolean)
      this.event.report = [...this.event.report, ...correctCustomReports]
    }
    this.modifyEventReportData(this.event)
  },
  methods: {
    async openPrint() {
      setTimeout(() => {
        window.print()
      }, 1)
    },
    formatDate(date) {
      return moment(date).format('DD. MM. YYYY')
    },
    round(num) {
      return round(num, 2)
    },
    formatDateTime(date) {
      return moment(date).format('DD. MM. YYYY HH:mm')
    },
    getYear(date) {
      return moment(date).year()
    },
    calculateOrderPrice(order) {
      return order.purchases.reduce((acc, purchase) => {
        return acc + parseFloat(purchase.orderItem.price)
      }, 0)
    },
    async getPurchasesByEvent(eventId) {
      const { data, errors } = await this.$apollo.query({
        query: GET_THEATER_EVENT_REPORT,
        variables: { eventId },
      })
      if (errors) {
        return
      } else {
        return data.getTheaterEventReport
      }
    },
    async modifyEventReportData(eventReport) {
      this.ordersWithDescription = eventReport.ordersWithDescription
      this.reportData = eventReport.report.filter(r => {
        return r.total !== '0.00'
      })
      this.orderFormOrders = eventReport.orderFormOrders
      this.refundOrders = eventReport.refundOrders
      this.gratisOrders = eventReport.gratisOrders
      this.totalGratisCount = this.gratisOrders.reduce((acc, order) => {
        return acc + order.purchases.length
      }, 0)
      this.totalRefundedCount = this.refundOrders.reduce((acc, order) => {
        return acc + order.purchases.length
      }, 0)
      this.totalRefundedAmount = this.refundOrders.reduce((acc, order) => {
        return acc + this.calculateOrderPrice(order)
      }, 0)
      this.totalOrderFormCount = this.orderFormOrders.reduce((acc, report) => {
        return acc + report.purchases.length
      }, 0)
      this.event = eventReport.event
      this.statistics = eventReport.statistics
      for (const report of this.reportData) {
        this.totalSoldAmount += parseFloat(report.total)
        const totalCount = parseInt(report.offline) + parseInt(report.online)
        this.totalSoldCount += totalCount
        if (!report.isOrderForm) {
          this.totalOfflineSoldAmount += parseInt(report.offline) ? Number(report.total) : 0
          this.totalOnlineSoldAmount += parseInt(report.online) ? Number(report.total) : 0
          this.totalOfflineSoldCount += parseInt(report.offline)
          this.totalOnlineSoldCount += parseInt(report.online)
        } else {
          this.totalOrderFormSoldAmount += Number(report.total)
        }
      }
      const offlineByPaymentType = eventReport.offlineSoldTicketsByPaymentMethod
      const offlineCash = offlineByPaymentType.find(item => item.paymentType === 'CASH')
      if (offlineCash) {
        this.offlineRecapitulationCash = {
          count: offlineCash.count,
          total: offlineCash.total,
        }
      }
      this.offlineRecapitulationCards = offlineByPaymentType
        .filter(item => item.paymentType === 'CARD')
        .map(item => {
          this.offlineRecapitulationCardsAll.count += Number(item.count)
          this.offlineRecapitulationCardsAll.total = this.round(
            this.offlineRecapitulationCardsAll.total + Number(item.total),
          )

          return {
            bankCardType: item.bankCard,
            count: item.count,
            total: item.total,
          }
        })
    },
  },
  apollo: {
    getOrganization: {
      query: GET_ORGANIZATION,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
        }
      },
      manual: true,
      result({ data, loading }) {
        if (loading || !data.getOrganization) {
          return
        }
        this.organization = data.getOrganization
      },
    },
  },
}
</script>

<style scoped>
.main-button {
  width: 100px;
}

@media print {
  .main-button {
    display: none !important;
  }
}

.page-container {
  width: 210mm;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.table-info {
  width: 50%;
}

canvas {
  height: 230px !important;
}

.divider {
  height: 1px;
  width: 100%;
  background: #0277bd;
}

.table-info section {
  display: flex;
  justify-content: space-between;
}

.table-info section p {
  font-size: 12px;
}

.page-title {
  color: #0277bd;
  text-align: center;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.72px;
}

.organization-data {
  color: #0277bd;
  font-family: Source Sans Pro;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%;
}

.basic {
  color: #0277bd;
  font-family: Source Sans Pro;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}

.header-data {
  color: #333;
  font-family: Source Sans Pro;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  padding: 30px 10px 10px;
  line-height: 120%;
  flex: 1;
}

.report-data {
  color: #333;
  font-family: Source Sans Pro;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  padding: 10px;
  line-height: 120%;
  flex: 1;
}

.report-row {
  display: flex;
}

.date {
  color: #0277bd;
  text-align: right;
  font-family: Source Sans Pro;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}

.report-title {
  border-radius: 4px 4px 0px 0px;
  background: #f5f5f5;
  display: flex;
}

.report-title p {
  flex: 1;
}

.text-right {
  text-align: right;
}
.order-with-description {
  display: flex;
  align-items: center;
  border-top: 1px solid #f5f5f5;
  border-radius: 0;
}
</style>
